import React, { useState, useMemo, useCallback, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import GeneralDataContainer from './generalData/components/GeneralDataContainer';
import InsuranceDataContainer from './insuranceData/components/InsuranceDataContainer';
import DriversContainer from './driverData/components/DriversContainer';
import InsuredClientContainer from './partner/components/InsuredClientContainer';
import InsuranceAssetContainer from './asset/components/InsuranceAssetContainer';
import ActionsContainer from './actions/components/ActionsContainer';
import RtiContainer from 'features/insuranceDocument/edit/general/insuranceData/components/RtiContainer';
import { useInsuranceTypeConfiguration, useQueryWithSimpleErrorHandling, useDictionaryHandling, useToast, useAuthorizations } from 'hooks';
import { INSURANCE_DATA_QUERY, INSURANCE_TYPES_QUERY } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { INSURANCE_DOCUMENT_UPDATED } from 'features/insuranceDocument/subscriptions/insuranceDocumentSubscriptions';
import { useTranslation } from 'react-i18next';
import { insuranceTypeNames } from 'constants/defaultValues';
import { useSubscription } from '@apollo/client';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

const InsuranceDocumentContainer = ({ isNew, documentId }) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const authorizations = useAuthorizations(
    [AuthorizationCodes.hasRightToViewInsuredOrUser, AuthorizationCodes.hasRightToViewDrivers, AuthorizationCodes.hasRightToViewVehicle],
    documentTypeId,
    documentStateId
  );
  const addToast = useToast();
  const { t } = useTranslation();
  const { globalReadOnly } = useContext(InsuranceDataContext);
  const { rcaInsuranceTypeConfiguration, cascoInsuranceTypeConfiguration, rtiInsuranceTypeConfiguration } = useInsuranceTypeConfiguration(
    new Array(insuranceTypeNames.RCA, insuranceTypeNames.CASCO, insuranceTypeNames.RTI)
  );

  const dirtyInfoRef = useRef({
    generalData: false,
    insuranceData: false,
    partnerData: false,
    driverData: false,
    assetData: false
  });

  const [insuranceData, setInsuranceData] = useState({});

  const { loading } = useQueryWithSimpleErrorHandling(INSURANCE_DATA_QUERY, {
    variables: { documentId },
    skip: isNew,
    onCompleted: data => {
      setInsuranceData(data?.insuranceData);
    },
    notifyOnNetworkStatusChange: true
  });

  const { getCodeById } = useDictionaryHandling(INSURANCE_TYPES_QUERY, 'insuranceTypes');
  const insuranceTypeCode = useMemo(() => getCodeById(insuranceData.insuranceTypeId), [getCodeById, insuranceData.insuranceTypeId]);

  useSubscription(INSURANCE_DOCUMENT_UPDATED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data: { data } }) => {
      setInsuranceData(data?.insuranceDocumentUpdated.insuranceData);
      const { silentMode } = data.insuranceDocumentUpdated.insuranceData;
      if (!silentMode) {
        addToast(t('General.Saved'), 'success', { toastId: 'insuranceDocumentUpdatedToast' });
      }
    }
  });

  const readOnly = useCallback(
    ({ customValidationFunction, controlName, hasAuthorization = null } = {}) => {
      if (
        globalReadOnly({ customValidationFunction, controlName, checkQuotationsStatus: false, hasAuthorization }) ||
        insuranceData.currentPolicyMaintained === true
      )
        return true;

      return false;
    },
    [globalReadOnly, insuranceData.currentPolicyMaintained]
  );

  const setDirtyInfoRef = (prop, value) => {
    dirtyInfoRef.current = {
      ...dirtyInfoRef.current,
      [prop]: value
    };
  };

  return (
    <React.Fragment>
      <GeneralDataContainer
        insuranceData={insuranceData}
        insuranceTypeCode={insuranceTypeCode}
        cascoInsuranceTypeCode={cascoInsuranceTypeConfiguration?.PropertyCode}
        isNew={isNew}
        dirtyInfoRef={dirtyInfoRef}
        setDirtyInfoRef={setDirtyInfoRef}
      />
      {(insuranceTypeCode === rcaInsuranceTypeConfiguration?.PropertyCode ||
        insuranceTypeCode === cascoInsuranceTypeConfiguration?.PropertyCode) &&
        isNew === false && (
          <>
            <InsuranceDataContainer
              insuranceData={insuranceData}
              rcaInsuranceTypeCode={rcaInsuranceTypeConfiguration?.PropertyCode}
              cascoInsuranceTypeCode={cascoInsuranceTypeConfiguration?.PropertyCode}
              readOnly={readOnly}
              dirtyInfoRef={dirtyInfoRef}
              setDirtyInfoRef={setDirtyInfoRef}
            />
            {authorizations?.hasRightToViewInsuredOrUser && (
              <InsuredClientContainer
                documentId={documentId}
                insuredId={insuranceData?.insuredId}
                clientId={insuranceData?.clientId}
                financingType={insuranceData?.financingType}
                insuranceTypeId={insuranceData?.insuranceTypeId}
                loaded={!loading}
                globalReadOnly={readOnly} // globalReadOnly not renamed because PartnerContainer defines another readOnly function
                dirtyInfoRef={dirtyInfoRef}
                setDirtyInfoRef={setDirtyInfoRef}
              />
            )}
            {insuranceTypeCode === rcaInsuranceTypeConfiguration?.PropertyCode && authorizations?.hasRightToViewDrivers && (
              <DriversContainer documentId={documentId} readOnly={readOnly} dirtyInfoRef={dirtyInfoRef} setDirtyInfoRef={setDirtyInfoRef} />
            )}
            {authorizations?.hasRightToViewVehicle && (
              <InsuranceAssetContainer
                documentId={documentId}
                assetId={insuranceData?.assetId}
                currencyCode={insuranceData?.currencyCode}
                insuranceTypeId={insuranceData?.insuranceTypeId}
                offerTypeId={insuranceData?.offerTypeId}
                startDate={insuranceData?.startDate}
                financingDocumentId={insuranceData?.financingDocumentId}
                readOnly={readOnly}
                dirtyInfoRef={dirtyInfoRef}
                setDirtyInfoRef={setDirtyInfoRef}
              />
            )}
            <ActionsContainer
              documentId={documentId}
              insuranceTypeId={insuranceData?.insuranceTypeId}
              readOnly={readOnly({ controlName: 'ViewOffers' })}
              dirtyInfoRef={dirtyInfoRef}
            />
          </>
        )}
      {insuranceTypeCode === rtiInsuranceTypeConfiguration?.PropertyCode && (
        <RtiContainer insuranceData={insuranceData} loading={loading} />
      )}
    </React.Fragment>
  );
};

InsuranceDocumentContainer.propTypes = {
  documentId: PropTypes.number,
  isNew: PropTypes.bool
};

export default InsuranceDocumentContainer;
